import { useEffect, useState } from "react";
import { ColumnsType } from "antd/es/table";
import { Switch } from "antd";
import { cellWidth } from "../../../core/methods/tableCellWidth";
import { useNavigate } from "react-router";
import { all_routes } from "../../router/all_routes";
import FiltersTrackCase, {
  FiltersSelectType,
  initialFilterSelect,
  SearchFormDataType,
} from "./FiltersTrackCase";
import Datatable from "../../../core/common/dataTable/index";
import TimelineModal from "./TimelineModal";
import {
  parseDate,
  parseNumber,
  safeLocaleCompare,
} from "../../../core/methods/sorterMethods";
import useApi from "../../../core/customhook/useApi";
import webservice_url, {
  apibaseurl,
} from "../../../core/webservice/Webservice_url";
import { formattedDate } from "../../../core/methods/formattedDate";
import Loading from "../../../core/common/custom-loading/Loading";
import { getSearchableHeader } from "../../../core/utils/getSearchableHeader";
import Swal from "sweetalert2";
import { exportAntdTableToExcel } from "../../../core/utils/exportExcel";
import { filterByColumnSearch } from "../../admin/utils/filterByColumnSearch";

export interface CaseTableRecord {
  key: string;
  sno: number;
  applicant_name: string;
  case_id: string;
  case_spoc: string;
  employee_id: string;
  case_received_date: string;
  sla_start_date: string;
  due_date: string;
  total_checks: number;
  work_in_progress: number;
  completed: number;
  stop: number;
  insuff_expire: string;
  actions: string;
}

export const actionOptions = [
  { value: "stop_case", label: "Stop Case" },
  { value: "stop_check", label: "Stop Check" },
  { value: "add_check", label: "Add Check" },
  { value: "generate_report", label: "Generate Report" },
];

type Props = {
  status_id: string;
};

type ReportVisibility = {
  assisted: boolean;
  nonAssisted: boolean;
};

const WorkInProgress: React.FC<Props> = ({ status_id }) => {
  const navigate = useNavigate();

  const [itemList, setItemList] = useState<any[]>([]);

  const [searchFilter, setSearchFilter] = useState<SearchFormDataType>({
    case_id: "",
    applicant_name: "",
    email: "",
    from_date: "",
    to_date: "",
  });

  const [searchTrigger, setSearchTrigger] = useState(0);

  const [filterData, setFilterData] =
    useState<FiltersSelectType>(initialFilterSelect);

  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const [timelineData, setTimelineData] = useState<any[]>([]);
  const [timelineLoading, setTimelineLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 1000,
    total: 0,
  });

  const [reportVisibility, setReportVisibility] = useState<
    Record<string, ReportVisibility>
  >({});

  const {
    callApi: handlesSearch,
    data: dataList,
    loading: searchLoading,
  } = useApi(webservice_url.search_live_case_progress, {
    method: "POST",
    autoCall: false,
  });

  // Show Report API
  const showReportAPI = useApi<any>(webservice_url.upload_assistedReports, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });
  const getCaseLog = useApi<any>(webservice_url.ViewLogAPI, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const handleViewLog = async (caseId?: string) => {
    if (!caseId) return;
    setShowTimeline(true);
    setTimelineLoading(true);
    try {
      const res = await getCaseLog.callApi({ case_id: caseId });
      setTimelineData(res?.data ?? []);
    } catch (err) {
      console.error(err);
      setTimelineData([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  const handleTableChange = async (pagination: any) => {
    setPagination((prev) => ({
      ...prev,
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };
  const fetchData = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
  ) => {
    const payload = {
      status: status_id || null,
      org_ids: filterData?.organization || null,
      project_ids: filterData?.project || null,
      program_ids: filterData?.program || null,
      department_ids: filterData?.departments || null,
      page,
      limit: pageSize,
    };

    try {
      const response = await handlesSearch(payload);

      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: response?.data?.total || 0,
      }));
    } catch (error) {
      console.error("API Failed", error);
    }
  };

  const DownloadAssestedReport = useApi<any>("", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
    responseType: "blob",
  });

  const handleDownloadReport = async (
    assignmentId: any,
    reportType: "assisted" | "non-assisted",
  ) => {
    const assignmentID = assignmentId?.assignment_id;

    try {
      const response: any = await DownloadAssestedReport.callApi(
        undefined,
        `${webservice_url.DownloadAssestedReport}/${assignmentID}/${reportType}-report/download-pdf`,
      );

      const blob = new Blob([response], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${reportType}_Report_${assignmentID}.pdf`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(`${reportType} Report Download Error:`, error);
    }
  };

  const [inventoryColumnSearch, setInventoryColumnSearch] = useState<
    Record<string, string>
  >({});
 
  const filteredData = filterByColumnSearch(
    Array.isArray(itemList) ? itemList : [],
    inventoryColumnSearch,
    {
      assisted_checks: (r) =>
        `${r.verified_assisted_checks ?? 0} / ${r.assisted_checks ?? 0}`,
      non_assisted_checks: (r) =>
        `${r.verified_non_assisted_checks ?? 0} / ${r.non_assisted_checks ?? 0}`,
    },
  );

  const handleDownloadDrugReport = async (doc: any, record: any) => {
    const path = doc?.file_name;
    if (!path) return;

    const fileUrl = /^https?:\/\//i.test(path)
      ? path
      : `${apibaseurl}/uploads/${path}`;

    // extension original file se
    const ext = (doc.original_name || path).split(".").pop();

    // applicant_name + case_id, safe filename
    const baseName = [record?.applicant_name, record?.case_id]
      .filter(Boolean)
      .join("_")
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^\w\-]/g, "");

    const fileName = baseName
      ? `${baseName}.${ext}`
      : doc.original_name || path;

    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error("Failed to download report");

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Drug report download failed", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to download the drug report.",
      });
    }
  };

  const handleReportVisibility = async (
    record: any,
    type: "assisted" | "non-assisted",
    checked: boolean,
  ) => {
    const assignmentId = record?.assignment_id;

    if (!assignmentId) return;

    const id = String(assignmentId);

    const current = reportVisibility[id] || {
      assisted: false,
      nonAssisted: false,
    };

    const payload = [
      {
        assignment_id: assignmentId,
        show_assisted_report: type === "assisted" ? checked : current.assisted,

        show_nonassisted_report:
          type === "non-assisted" ? checked : current.nonAssisted,
      },
    ];

    try {
      console.log("Show Report Payload:", payload);

      await showReportAPI.callApi(payload);

      // Update UI only after successful API call
      setReportVisibility((prev) => ({
        ...prev,
        [id]: {
          assisted: type === "assisted" ? checked : current.assisted,

          nonAssisted: type === "non-assisted" ? checked : current.nonAssisted,
        },
      }));
    } catch (error) {
      console.error("Show Report Update Error:", error);
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "serial_number",
      dataIndex: "serial_number",
      onCell: () => cellWidth(100),
      align: "center",
    },

    {
      title: getSearchableHeader(
        "Applicant Name",
        "applicant_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "applicant_name",
      key: "applicant_name",
     onCell: () => cellWidth(200),
      render: (text, record: any) => (
        <div
          className="wip-link-cell"
          onClick={() => {
            navigate(all_routes.verifier_caseDetails, {
              state: {
                case_id: record.case_id,
                assignment_id: record.assignment_id,
              },
            });
          }}
        >
          {text}
        </div>
      ),
      sorter: (a: any, b: any) =>
        (a.candidate_name ?? "").localeCompare(b.applicant_name ?? ""),
    },

    {
      title: getSearchableHeader(
        "Candidte Id",
        "hrms_id",
        setInventoryColumnSearch,
      ),
      dataIndex: "hrms_id",
      key: "hrms_id",
      onCell: () => cellWidth(200),
      sorter: (a, b) => safeLocaleCompare(a, b, "hrms_id"),
    },

    {
      title: getSearchableHeader(
        "Case Id",
        "case_id",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_id",
      key: "case_id",
      onCell: () => cellWidth(200),
      sorter: (a, b) => safeLocaleCompare(a, b, "case_id"),
    },

    {
      title: getSearchableHeader(
        "Case Spoc",
        "case_spoc",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_spoc",
      key: "case_spoc",
      onCell: () => cellWidth(200),
      sorter: (a, b) => safeLocaleCompare(a, b, "case_spoc"),
    },
    {
      title: getSearchableHeader(
        "Case Status",
        "overall_status",
        setInventoryColumnSearch,
      ),
      dataIndex: "overall_status",
      key: "overall_status",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) =>
        (a.overall_status ?? "").localeCompare(b.overall_status ?? ""),
      render: (text: any, record: any) => {
        return (
          <span
            style={{
              backgroundColor: record?.overall_color_value || "#6c757d",
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "4px",
              display: "inline-block",
              fontSize: "12px",
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {text ?? "-"}
          </span>
        );
      },
    },
    {
      title: getSearchableHeader(
        "Project",
        "project_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "project_name",
      key: "project_name",
      onCell: () => cellWidth(200),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.project_name ?? "").localeCompare(b.project_name ?? ""),
    },

    {
      title: getSearchableHeader(
        "Program",
        "program_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "program_name",
      key: "program_name",
      onCell: () => cellWidth(200),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.program_name ?? "").localeCompare(b.program_name ?? ""),
    },

    {
      title: getSearchableHeader(
        "Department",
        "department_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "department_name",
      key: "department_name",
      onCell: () => cellWidth(200),
      width: "220px",
      sorter: (a: any, b: any) =>
        (a.department_name ?? "").localeCompare(b.department_name ?? ""),
    },

    {
      title: getSearchableHeader(
        "Case Received Date",
        "case_received_date",
        setInventoryColumnSearch,
      ),
      dataIndex: "case_received_date",
      key: "case_received_date",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) =>
        parseDate(a.case_received_date).getTime() -
        parseDate(b.case_received_date).getTime(),
      render: (text) => formattedDate(text),
    },

    {
      title: getSearchableHeader(
        "SLA Start Date",
        "sla_start_date",
        setInventoryColumnSearch,
      ),
      dataIndex: "sla_start_date",
      key: "sla_start_date",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) =>
        parseDate(a.sla_start_date).getTime() -
        parseDate(b.sla_start_date).getTime(),
      render: (text) => formattedDate(text),
    },

    {
      title: getSearchableHeader(
        "Due Date",
        "due_date",
        setInventoryColumnSearch,
      ),
      dataIndex: "due_date",
      key: "due_date",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) =>
        parseDate(a.due_date).getTime() - parseDate(b.due_date).getTime(),
      render: (text) => formattedDate(text),
    },

    {
      title: getSearchableHeader(
        "Total Checks",
        "total_checks",
        setInventoryColumnSearch,
      ),
      dataIndex: "total_checks",
      key: "total_checks",
      align: "center",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) =>
        parseNumber(a.total_checks) - parseNumber(b.total_checks),
    },

    {
      title: getSearchableHeader(
        "Assisted",
        "assisted_checks",
        setInventoryColumnSearch,
      ),
      dataIndex: "assisted_checks",
      key: "assisted_checks",
      onCell: () => cellWidth(200),
      align: "center",
      sorter: (a, b) => Number(a.assisted_checks) - Number(b.assisted_checks),
      render: (_value: number, record: any) => {
        const verified = Number(record.assisted_verified_checks ?? 0);
        const total = Number(record.assisted_checks ?? 0);
        const isComplete = verified === total;

        return (
          <div>
            <span className="wip-chip wip-chip-progress">
              {verified} / {total}
            </span>

            <div
              className={`mt-1 ${isComplete ? "text-success" : "text-warning"}`}
            >
              {isComplete ? "Complete" : "In Progress"}
            </div>
          </div>
        );
      },
    },

    {
      title: getSearchableHeader(
        "Non-Assisted",
        "non_assisted_checks",
        setInventoryColumnSearch,
      ),
      dataIndex: "non_assisted_checks",
      key: "non_assisted_checks",
      onCell: () => cellWidth(200),
      align: "center",
      sorter: (a, b) =>
        Number(a.non_assisted_checks) - Number(b.non_assisted_checks),
      render: (_value: number, record: any) => {
        const verified = Number(record.non_assisted_verified_checks ?? 0);
        const total = Number(record.non_assisted_checks ?? 0);
        const isComplete = verified === total;

        return (
          <div>
            <span className="wip-chip wip-chip-progress">
              {verified} / {total}
            </span>

            <div
              className={`mt-1 ${isComplete ? "text-success" : "text-warning"}`}
            >
              {isComplete ? "Complete" : "In Progress"}
            </div>
          </div>
        );
      },
    },

    {
      title: getSearchableHeader(
        "Candidate Submited",
        "candidate_submit",
        setInventoryColumnSearch,
      ),
      dataIndex: "candidate_submit",
      key: "candidate_submit",
      onCell: () => cellWidth(200),
      align: "center",
      sorter: (a, b) => a.candidate_submit - b.candidate_submit,
      render: (value: number) => (
        <span className="wip-chip wip-chip-success">{value ?? 0}</span>
      ),
    },

    {
      title: getSearchableHeader(
        "Work In Progress",
        "work_in_progress",
        setInventoryColumnSearch,
      ),
      dataIndex: "work_in_progress",
      key: "work_in_progress",
      align: "center",
      sorter: (a: any, b: any) =>
        parseNumber(a.work_in_progress) - parseNumber(b.work_in_progress),
    },

    {
      title: getSearchableHeader(
        "Completed",
        "completed",
        setInventoryColumnSearch,
      ),
      dataIndex: "completed",
      key: "completed",
      align: "center",
      sorter: (a: any, b: any) =>
        parseNumber(a.completed) - parseNumber(b.completed),
    },

    {
      title: getSearchableHeader("Stop", "stopped", setInventoryColumnSearch),
      dataIndex: "stopped",
      key: "stop",
      align: "center",
      sorter: (a: any, b: any) => parseNumber(a.stop) - parseNumber(b.stop),
    },

    // {
    //   title: getSearchableHeader(
    //     "Insuff Expire",
    //     "insufficiency_expired",
    //     setInventoryColumnSearch,
    //   ),
    //   dataIndex: "insufficiency_expired",
    //   key: "insufficiency_expired",
    //   onCell: () => cellWidth(200),
    //   align: "center",
    //   sorter: (a: any, b: any) =>
    //     parseNumber(a.insufficiency_expired) -
    //     parseNumber(b.insufficiency_expired),
    // },

    {
      title: "Show Assisted Report",
      dataIndex: "",
      key: "show_assisted_report",
      onCell: () => cellWidth(200),
      align: "center",
      render: (_: any, record: any) => {
        const assignmentId = String(record?.assignment_id ?? "");

        const isChecked = reportVisibility[assignmentId]?.assisted ?? false;

        return (
          <Switch
            checked={isChecked}
            loading={showReportAPI.loading}
            onChange={(checked) =>
              handleReportVisibility(record, "assisted", checked)
            }
          />
        );
      },
    },

    {
      title: "Show Non-assisted Report",
      dataIndex: "",
      key: "show_nonassisted_report",
      onCell: () => cellWidth(200),
      align: "center",
      render: (_: any, record: any) => {
        const assignmentId = String(record?.assignment_id ?? "");

        const isChecked = reportVisibility[assignmentId]?.nonAssisted ?? false;

        return (
          <Switch
            checked={isChecked}
            loading={showReportAPI.loading}
            onChange={(checked) =>
              handleReportVisibility(record, "non-assisted", checked)
            }
          />
        );
      },
    },

    {
      title: "Assisted Report",
      dataIndex: "",
      onCell: () => cellWidth(200),
      render: (_: any, record: any) => (
        <button
          className="btn btn-sm btn-primary d-flex"
          onClick={() => handleDownloadReport(record, "assisted")}
        >
          <span className="wip-btn-label">Download</span>
        </button>
      ),
    },

    {
      title: "Non-assisted Report",
      dataIndex: "",
      onCell: () => cellWidth(200),
      render: (_: any, record: any) => (
        <button
          className="btn btn-sm btn-primary d-flex"
          onClick={() => handleDownloadReport(record, "non-assisted")}
        >
          <span className="wip-btn-label">Download</span>
        </button>
      ),
    },

    {
      title: getSearchableHeader(
        "Drug Test",
        "drug_test_status",
        setInventoryColumnSearch,
      ),
      dataIndex: "drug_test_status",
      key: "drug_test_status",
      align: "center",
      sorter: (a, b) => a.drug_test_status - b.drug_test_status,
    },
    {
      title: "Drug Report",
      dataIndex: "drug_report",
      key: "drug_report",
      align: "center",
      onCell: () => cellWidth(200),
      sorter: (a, b) =>
        (a.drug_report?.length ?? 0) - (b.drug_report?.length ?? 0),
      render: (docs: any[] | null, record: any) =>
        Array.isArray(docs) && docs.length > 0 ? (
          <div className="d-flex flex-column align-items-center gap-1">
            {docs.map((doc) => (
              <div
                key={doc.document_id}
                className="d-flex align-items-center gap-2"
              >
                <span
                  onClick={() => handleDownloadDrugReport(doc, record)}
                  className="wip-chip wip-chip-success"
                  style={{ cursor: "pointer" }}
                >
                  {doc.original_name ?? doc.file_name}{" "}
                  <i className="ti ti-download" />
                </span>
              </div>
            ))}
          </div>
        ) : (
          "-"
        ),
    },

    {
      title: "View Log",
      key: "viewLog",
      onCell: () => cellWidth(110),
      render: (_: any, record: any) => (
        <div
          className="text-center text-dark"
          style={{ cursor: "pointer" }}
          onClick={() => handleViewLog(record.case_id)}
        >
          <i className="ti ti-eye"></i>
        </div>
      ),
    },
  ];
  useEffect(() => {
    if (dataList?.data?.rows) {
      const rows = dataList.data.rows;

      setItemList(rows); 
      setReportVisibility((prev) => {
        const updated = { ...prev };

        rows.forEach((record: any) => {
          const assignmentId = record?.assignment_id; 
          if (!assignmentId) return; 
          const id = String(assignmentId); 
          if (!updated[id]) {
            updated[id] = {
              assisted:
                record?.show_assisted_report === true ||
                record?.show_assisted_report === 1,

              nonAssisted:
                record?.show_nonassisted_report === true ||
                record?.show_nonassisted_report === 1,
            };
          }
        });

        return updated;
      });
    }
  }, [dataList]);

  useEffect(() => {
    fetchData(1, pagination.pageSize);
  }, [searchTrigger]);

  const handleDownloadExcel = () => {
    exportAntdTableToExcel({
      columns,
      dataSource: filteredData,
      fileName: "Work_In_Progress_Report",
      sheetName: "Work In Progress",
    });
  };

  const loading = searchLoading;
  return (
    <div className="px-2">
      {/* Filters */}
      <FiltersTrackCase
        filterData={filterData}
        searchFilter={searchFilter}
        setFilterData={setFilterData}
        setSearchFilter={setSearchFilter}
        onSearch={() => setSearchTrigger((prev) => prev + 1)}
      />

      {/* Table */}
      <div className="card mt-3">
        {searchLoading ? (
          <Loading />
        ) : (
          <>
            <div className="d-flex justify-content-end pe-2 py-2 pb-0">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleDownloadExcel}
                disabled={loading}
              >
                <i className="ti ti-download" /> Export to Excel
              </button>
            </div>
            <Datatable
              dataSource={filteredData}
              columns={columns}
              Selection={false}
              fixedHeader
              tableHeight={650}
              rowKey="case_id"
              pagination={pagination}
              handleTableChange={handleTableChange}
            />
          </>
        )}
      </div>

      {/* Timeline Modal */}
      {showTimeline && (
        <TimelineModal
          timelineData={timelineData}
          loading={timelineLoading}
          onClose={() => {
            setShowTimeline(false);
            setTimelineData([]);
          }}
        />
      )}
    </div>
  );
};

export default WorkInProgress;

// import { useEffect, useState } from "react";
// import { ColumnsType } from "antd/es/table";
// import { cellWidth } from "../../../core/methods/tableCellWidth";
// import { useNavigate } from "react-router";
// import { all_routes } from "../../router/all_routes";
// import FiltersTrackCase, {
//   FiltersSelectType,
//   initialFilterSelect,
//   SearchFormDataType,
// } from "./FiltersTrackCase";
// import Datatable from "../../../core/common/dataTable/index";
// import TimelineModal from "./TimelineModal";
// import {
//   dummyData_WorkInProgress,
//   timelineData_invite,
// } from "../../../core/data/js/dummy-data";
// import {
//   parseDate,
//   parseNumber,
//   safeLocaleCompare,
// } from "../../../core/methods/sorterMethods";
// import useApi from "../../../core/customhook/useApi";
// import webservice_url from "../../../core/webservice/Webservice_url";
// import { formattedDate } from "../../../core/methods/formattedDate";
// import Loading from "../../../core/common/custom-loading/Loading";

// export interface CaseTableRecord {
//   key: string;
//   sno: number;
//   applicant_name: string;
//   case_id: string;
//   case_spoc: string;
//   employee_id: string;
//   case_received_date: string;
//   sla_start_date: string;
//   due_date: string;
//   total_checks: number;
//   work_in_progress: number;
//   completed: number;
//   stop: number;
//   insuff_expire: string;
//   actions: string;
// }

// export const actionOptions = [
//   { value: "stop_case", label: "Stop Case" },
//   { value: "stop_check", label: "Stop Check" },
//   { value: "add_check", label: "Add Check" },
//   { value: "generate_report", label: "Generate Report" },
// ];

// type Props = {
//   status_id: string;
// };

// const WorkInProgress: React.FC<Props> = ({ status_id }) => {
//   const navigate = useNavigate();
//   const [itemList, setItemList] = useState([]);

//   const [searchFilter, setSearchFilter] = useState<SearchFormDataType>({
//     case_id: "",
//     applicant_name: "",
//     email: "",
//     from_date: "",
//     to_date: "",
//   });
//   const [searchTrigger, setSearchTrigger] = useState(0);
//   const [filterData, setFilterData] =
//     useState<FiltersSelectType>(initialFilterSelect);

//   const [showTimeline, setShowTimeline] = useState<boolean>(false);

//   const [pagination, setPagination] = useState({
//     current: 1,
//     pageSize: 1000,
//     total: 0,
//   });

//   // API
//   const {
//     callApi: handlesSearch,
//     data: dataList,
//     loading: searchLoading,
//   } = useApi(webservice_url.search_live_case_progress, {
//     method: "POST",
//     autoCall: false,
//   });

//   const handleTableChange = async (pagination: any) => {
//     setPagination((prev) => ({
//       ...prev,
//       current: pagination.current,
//       pageSize: pagination.pageSize,
//     }));
//   };

//   const fetchData = async (
//     page = pagination.current,
//     pageSize = pagination.pageSize,
//   ) => {
//     const payload = {
//       status: status_id || null,
//       org_ids: filterData?.organization || null,
//       project_ids: filterData?.project || null,
//       program_ids: filterData?.program || null,
//       department_ids: filterData?.departments || null,
//       page,
//       limit: pageSize,
//     };

//     try {
//       const response = await handlesSearch(payload);

//       setPagination((prev) => ({
//         ...prev,
//         current: page,
//         pageSize,
//         total: response?.data?.total || 0,
//       }));
//     } catch (error) {
//       console.error("API Failed", error);
//     }
//   };

//     const DownloadAssestedReport = useApi<any>("", {
//     method: "GET",
//     autoCall: false,
//     handleGlobalError: false,
//     responseType: "blob",
//   });
//     const handleDownloadReport = async (
//     assignmentId: any,
//     reportType: "assisted" | "non-assisted",
//   ) => {
//     const assignmentID = assignmentId?.assignment_id;

//     try {
//       const response: any = await DownloadAssestedReport.callApi(
//         undefined,
//         `${webservice_url.DownloadAssestedReport}/${assignmentID}/${reportType}-report/download-pdf`,
//       );

//       const blob = new Blob([response], {
//         type: "application/pdf",
//       });

//       const url = window.URL.createObjectURL(blob);

//       const link = document.createElement("a");
//       link.href = url;
//       link.download = `${reportType}_Report_${assignmentID}.pdf`;

//       document.body.appendChild(link);
//       link.click();

//       document.body.removeChild(link);
//       window.URL.revokeObjectURL(url);
//     } catch (error) {
//       console.log(`${reportType} Report Download Error:`, error);
//     }
//   };

//   const columns: ColumnsType<any> = [
//     {
//       title: "S.No",
//       key: "sno",
//       align: "center",
//       render: (_: any, _record: any, index: number) =>
//         (pagination.current - 1) * pagination.pageSize + index + 1,
//     },
//     {
//       title: "Applicant Name",
//       dataIndex: "applicant_name",
//       key: "applicant_name",
//       onCell: () => cellWidth(220),
//       render: (text, record: any) => (
//         <div
//           className="btn-link "
//           onClick={() => {
//             navigate(all_routes.verifier_caseDetails, {
//               state: {
//                 case_id: record.case_id,
//                 assignment_id: record.assignment_id,
//               },
//             });
//           }}
//         >
//           {text}
//         </div>
//       ),
//       sorter: (a: any, b: any) =>
//         (a.candidate_name ?? "").localeCompare(b.applicant_name ?? ""),
//     },
//     {
//       title: "Case Id",
//       dataIndex: "case_id",
//       key: "case_id",
//       onCell: () => cellWidth(190),
//       sorter: (a, b) => safeLocaleCompare(a, b, "case_id"),
//     },
//     {
//       title: "Case Spoc",
//       dataIndex: "case_spoc",
//       key: "case_spoc",
//       onCell: () => cellWidth(140),
//       sorter: (a, b) => safeLocaleCompare(a, b, "case_spoc"),
//     },
//     {
//       title: "Candidate Id",
//       dataIndex: "hrms_id",
//       key: "hrms_id",
//       onCell: () => cellWidth(160),
//       sorter: (a, b) => safeLocaleCompare(a, b, "hrms_id"),
//     },
//     {
//       title: "Case Received Date",
//       dataIndex: "case_received_date",
//       key: "case_received_date",
//       onCell: () => cellWidth(200),
//       sorter: (a: any, b: any) =>
//         parseDate(a.case_received_date).getTime() -
//         parseDate(b.case_received_date).getTime(),
//       render: (text) => formattedDate(text),
//     },
//     {
//       title: "SLA Start Date",
//       dataIndex: "sla_start_date",
//       key: "sla_start_date",
//       onCell: () => cellWidth(190),
//       sorter: (a: any, b: any) =>
//         parseDate(a.sla_start_date).getTime() -
//         parseDate(b.sla_start_date).getTime(),
//       render: (text) => formattedDate(text),
//     },
//     {
//       title: "Due Date",
//       dataIndex: "due_date",
//       key: "due_date",
//       onCell: () => cellWidth(140),
//       sorter: (a: any, b: any) =>
//         parseDate(a.due_date).getTime() - parseDate(b.due_date).getTime(),
//       render: (text) => formattedDate(text),
//     },
//     {
//       title: "Total Checks",
//       dataIndex: "total_checks",
//       key: "total_checks",
//       align: "center",
//       onCell: () => cellWidth(160),
//       sorter: (a: any, b: any) =>
//         parseNumber(a.total_checks) - parseNumber(b.total_checks),
//     },
//     {
//       title: "Work In Progress",
//       dataIndex: "work_in_progress",
//       key: "work_in_progress",
//       align: "center",
//       sorter: (a: any, b: any) =>
//         parseNumber(a.work_in_progress) - parseNumber(b.work_in_progress),
//     },
//     {
//       title: "Completed",
//       dataIndex: "completed",
//       key: "completed",
//       align: "center",
//       sorter: (a: any, b: any) =>
//         parseNumber(a.completed) - parseNumber(b.completed),
//     },
//     {
//       title: "Stop",
//       dataIndex: "stopped",
//       key: "stop",
//       align: "center",
//       sorter: (a: any, b: any) => parseNumber(a.stop) - parseNumber(b.stop),
//     },
//     {
//       title: "Insuff Expire",
//       dataIndex: "insufficiency_expired",
//       key: "insufficiency_expired",
//       onCell: () => cellWidth(150),
//       align: "center",
//       sorter: (a: any, b: any) =>
//         parseNumber(a.insufficiency_expired) -
//         parseNumber(b.insufficiency_expired),
//     },
//         {
//       title: "Assisted Report",
//       dataIndex: "",
//       onCell: () => cellWidth(190),
//       render: (_: any, record: any) => (
//         <button
//           className="btn btn-sm btn-primary d-flex gap-2"
//           onClick={() => handleDownloadReport(record, "assisted")}
//           // disabled={loading}
//         >
//           <i className="bi bi-download"></i>
//           <span className="wip-btn-label">Download</span>
//         </button>
//       ),
//     },
//     {
//       title: "Non-assisted Report",
//       dataIndex: "",
//       onCell: () => cellWidth(190),
//       render: (_: any, record: any) => (
//         <button
//           className="btn btn-sm btn-primary d-flex gap-2"
//           onClick={() => handleDownloadReport(record, "non-assisted")}
//           // disabled={loading}
//         >
//           <i className="bi bi-download"></i>
//           <span className="wip-btn-label">Download</span>
//         </button>
//       ),
//     },
//     // {
//     //   title: "Actions",
//     //   dataIndex: "",
//     //   onCell: () => cellWidth(190),
//     //   render: () => (
//     //     <>
//     //       <CommonSelect2
//     //         className="select "
//     //         options={actionOptions}
//     //       // defaultValue={actionOptions[0]}
//     //       // onChange={(option) => {}}
//     //       />
//     //     </>
//     //   ),
//     // },
//     // {
//     //   title: "View Log",
//     //   dataIndex: "",
//     //   onCell: () => cellWidth(100),
//     //   render: () => (
//     //     <div
//     //       className="text-center text-dark"
//     //       style={{ cursor: "pointer" }}
//     //       onClick={() => setShowTimeline(true)}
//     //     >
//     //       <i className="ti ti-eye "></i>
//     //     </div>
//     //   ),
//     // },
//   ];

//   useEffect(() => {
//     if (dataList?.data?.rows) {
//       setItemList(dataList.data.rows);
//     }
//   }, [dataList]);

//   useEffect(() => {
//     fetchData(1, pagination.pageSize);
//   }, [searchTrigger]);

//   return (
//     <div className="px-2">
//       {/* filters */}
//       <FiltersTrackCase
//         filterData={filterData}
//         searchFilter={searchFilter}
//         setFilterData={setFilterData}
//         setSearchFilter={setSearchFilter}
//         onSearch={() => setSearchTrigger((prev) => prev + 1)}
//       />

//       {/* Table  */}
//       <div className="card mt-3">
//         {searchLoading ? (
//           <Loading />
//         ) : (
//           <Datatable
//             dataSource={itemList}
//             columns={columns}
//             Selection={false}
//             rowKey="case_id"
//             pagination={pagination}
//             handleTableChange={handleTableChange}
//           />
//         )}
//       </div>

//       {/* Timeline Modal */}
//       {showTimeline && (
//         <TimelineModal
//           timelineData={timelineData_invite}
//           onClose={() => setShowTimeline(false)}
//         />
//       )}
//     </div>
//   );
// };

// export default WorkInProgress;
